// @ts-nocheck
/**
 * Operator / company name suggestions for service request forms
 * (water, grocery, tacos, waste, hotshots). Names are sourced from the
 * Permian basin company directory + common operators list.
 */
import names from '../data/operator-company-names.json';

const QA_OPERATOR_COMPANY = 'ZiaWellNav Operator Test';

/** Demo / QA poster names that must not appear in public operator pickers. */
export function isDemoOperatorName(name: string): boolean {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return true;
  if (n === 'wef' || n === 'ziawellnav') return true;
  if (n.includes('ziawellnav')) return true;
  if (n.includes('operator test')) return true;
  if (/\bqa\b/.test(n)) return true;
  if (/\btest\b/.test(n)) return true;
  return false;
}

export const OPERATOR_COMPANY_NAMES: string[] = [
  QA_OPERATOR_COMPANY,
  ...(Array.isArray(names)
    ? (names as string[]).filter((n) => typeof n === 'string' && n.trim().length > 0)
    : []),
];

/** Frequently used Permian operators shown when the field is empty / short query */
export const POPULAR_OPERATOR_COMPANIES = [
  QA_OPERATOR_COMPANY,
  'EOG Resources',
  'Chevron',
  'Devon Energy',
  'Occidental Petroleum',
  'Oxy (Permian)',
  'ConocoPhillips',
  'Diamondback Energy',
  'XTO Energy',
  'Apache Corporation',
  'Pioneer Natural Resources',
  'Mewbourne Oil Company',
  'Coterra Energy',
  'SM Energy',
  'Targa Resources',
  'Halliburton',
] as const;

/**
 * Ranked filter for free-solo company / operator autocomplete.
 * Prefers starts-with, then word starts-with, then substring.
 */
export function filterOperatorCompanyNames(
  query: string,
  limit = 12,
  source: readonly string[] = OPERATOR_COMPANY_NAMES,
): string[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    const fromSource = POPULAR_OPERATOR_COMPANIES.filter((n) =>
      source.some((s) => s.toLowerCase() === n.toLowerCase()),
    );
    if (fromSource.length) return fromSource.slice(0, limit);
    const unique: string[] = [];
    const seen = new Set<string>();
    for (const name of source) {
      const trimmed = String(name || '').trim();
      if (!trimmed || seen.has(trimmed.toLowerCase())) continue;
      seen.add(trimmed.toLowerCase());
      unique.push(trimmed);
      if (unique.length >= limit) break;
    }
    return unique;
  }

  const starts: string[] = [];
  const wordStarts: string[] = [];
  const includes: string[] = [];
  const seen = new Set<string>();

  for (const name of source) {
    if (!name || seen.has(name)) continue;
    const lower = name.toLowerCase();
    if (lower.startsWith(q)) {
      starts.push(name);
      seen.add(name);
    } else if (lower.split(/[\s,&./()\-]+/).some((w) => w.startsWith(q))) {
      wordStarts.push(name);
      seen.add(name);
    } else if (lower.includes(q)) {
      includes.push(name);
      seen.add(name);
    }
    if (starts.length >= limit) break;
  }

  return [...starts, ...wordStarts, ...includes].slice(0, limit);
}

export type OperatorLetterGroup = { letter: string; names: string[] };

/** Full directory grouped A–Z (leading digits/symbols under #). */
export function operatorCompanyNamesByLetter(
  source: readonly string[] = OPERATOR_COMPANY_NAMES,
): OperatorLetterGroup[] {
  const groups = new Map<string, string[]>();
  const seen = new Set<string>();
  for (const raw of source) {
    const name = String(raw || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const ch = name.charAt(0).toUpperCase();
    const letter = /[A-Z]/.test(ch) ? ch : '#';
    const list = groups.get(letter);
    if (list) list.push(name);
    else groups.set(letter, [name]);
  }
  const letters = [...groups.keys()].sort((a, b) => {
    if (a === '#') return -1;
    if (b === '#') return 1;
    return a.localeCompare(b);
  });
  return letters.map((letter) => ({
    letter,
    names: (groups.get(letter) || []).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
  }));
}
